import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useNotification } from '@/components/notifications';
import { WORKFLOW_ELIGIBILITY_QUERY_KEY } from '@/hooks/queries/use-workflow-eligibility';
import { ApplyResult, applyToWave } from '@/services/workflow/qualification';

interface UseApplyToWaveOptions {
  orgId: string | undefined;
  workflowId: string;
  workflowName: string;
  onAdmitted?: (result: ApplyResult) => void;
}

export const useApplyToWave = ({
  orgId,
  workflowId,
  workflowName,
  onAdmitted,
}: UseApplyToWaveOptions) => {
  const queryClient = useQueryClient();
  const notification = useNotification();

  return useMutation<ApplyResult, Error, { waveId: string }>({
    mutationFn: ({ waveId }) => {
      if (!orgId) throw new Error('No org ID');
      return applyToWave(orgId, workflowId, waveId);
    },
    onSuccess: (result) => {
      // Admission changes what a further application would return, so the
      // cached verdict is stale whether or not the gate admitted.
      queryClient.invalidateQueries({
        queryKey: [WORKFLOW_ELIGIBILITY_QUERY_KEY, orgId, workflowId],
      });
      if (result.admitted && result.placed) {
        notification.success(`${workflowName} entered pre-UAT`);
        onAdmitted?.(result);
      }
    },
    onError: (error) => {
      notification.error(error.message || 'Could not apply to the wave');
      Sentry.captureException(error, {
        extra: { context: 'apply-to-wave', workflowId, orgId },
      });
    },
  });
};
